import { motion } from "framer-motion";
import { Clock, Check } from "lucide-react";
import DynamicIcon from "./DynamicIcon";
import { BUSINESS_TYPES, PAIN_POINTS } from "../data/automationData";

export const PainPointSelector = ({
  businessType,
  setBusinessType,
  selectedPains,
  togglePain,
  onShowPlan,
}) => {
  return (
    <section
      id="wizard"
      className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 scroll-mt-20"
      data-testid="wizard-section"
    >
      {/* Step 1 */}
      <div className="mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Paso 1 · Elige tu negocio
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">
          ¿Qué tipo de negocio tienes?
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {BUSINESS_TYPES.map((b, i) => {
            const active = businessType === b.id;
            return (
              <motion.button
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => setBusinessType(b.id)}
                data-testid={`business-type-${b.id}`}
                className={`group flex items-center gap-3 p-5 rounded-2xl border-2 text-left transition-all ${
                  active
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-card hover:border-primary/30 hover:shadow-sm"
                }`}
              >
                <span
                  className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center transition-colors ${
                    active ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"
                  }`}
                >
                  <DynamicIcon name={b.icon} className="h-5 w-5" />
                </span>
                <span className="font-medium text-sm sm:text-base">{b.label}</span>
                {active && <Check className="h-4 w-4 text-primary ml-auto" />}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Step 2 */}
      <div
        className={`transition-all duration-500 ${
          businessType ? "opacity-100" : "opacity-40 pointer-events-none"
        }`}
      >
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Paso 2 · Elige qué te quita tiempo
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-2">
          ¿En qué se te va el día?
        </h2>
        <p className="text-muted-foreground mb-8">
          Elige una o varias. Crearemos un plan a tu medida.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PAIN_POINTS.map((p) => {
            const active = selectedPains.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => togglePain(p.id)}
                data-testid={`pain-point-${p.id}`}
                className={`flex items-start gap-4 p-6 rounded-2xl border-2 text-left transition-all ${
                  active
                    ? "border-accent bg-accent/5 shadow-sm"
                    : "border-border bg-card hover:border-accent/30 hover:shadow-sm"
                }`}
              >
                <span
                  className={`h-12 w-12 shrink-0 rounded-xl flex items-center justify-center ${
                    active ? "bg-accent text-accent-foreground" : "bg-secondary text-primary"
                  }`}
                >
                  <DynamicIcon name={p.icon} className="h-6 w-6" />
                </span>
                <span>
                  <span className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-lg">{p.label}</span>
                    {active && <Check className="h-4 w-4 text-accent" />}
                  </span>
                  <span className="block text-sm text-muted-foreground mt-1">{p.plain}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground mt-3">
                    <Clock className="h-3.5 w-3.5" />
                    ~{p.manualMinutes} min/día hoy
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <button
            disabled={!businessType || selectedPains.length === 0}
            onClick={onShowPlan}
            data-testid="show-plan-btn"
            className="bg-primary text-primary-foreground px-8 py-4 rounded-full font-semibold text-base hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
          >
            Ver mi plan de automatización →
          </button>
        </div>
      </div>
    </section>
  );
};
